'use client';

import { useState } from 'react';
import ServiceHero from './ServiceHero';
import ServiceCard from './ServiceCard';
import CertificationBadges from './CertificationBadges';
import ServiceComparison from './ServiceComparison';
import RegionalTestimonials from './RegionalTestimonials';
import ConsultationCTA from './ConsultationCTA';

interface Service {
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
  features: Array<{
    title: string;
    description: string;
  }>;
  priceRange: {
    inr: string;
    aed: string;
    usd: string;
  };
}

interface Certification {
  name: string;
  issuer: string;
  icon: string;
  region: string;
}

interface ComparisonFeature {
  name: string;
  basic: boolean | string;
  professional: boolean | string;
  enterprise: boolean | string;
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  region: string;
  image: string;
  alt: string;
  quote: string;
  impact: string;
}

const OurServicesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const services: Service[] = [
  {
    title: 'Carbon Credit Generation',
    description:
    'End-to-end carbon credit project development, from baseline assessment to verification and trading. We help you monetize your environmental initiatives while contributing to global climate goals.',
    image: "https://images.unsplash.com/photo-1523515105784-b7d4c142c7eb",
    alt: 'Aerial view of lush green forest canopy with sunlight filtering through dense trees',
    icon: 'CloudIcon',
    features: [
    {
      title: 'Project Development',
      description: 'Comprehensive feasibility studies and project design'
    },
    {
      title: 'Verification & Certification',
      description: 'Third-party verification and international certification'
    },
    {
      title: 'Carbon Trading Support',
      description: 'Market access and trading facilitation'
    },
    {
      title: 'Monitoring & Reporting',
      description: 'Continuous monitoring and transparent reporting'
    }],
    priceRange: {
      inr: '5,00,000',
      aed: '22,000',
      usd: '6,000'
    }
  },
  {
    title: 'ESG Reporting Solutions',
    description:
    'Comprehensive ESG compliance and reporting services that meet international standards. Transform your sustainability data into actionable insights and regulatory-compliant reports.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15a5e51a5-1764656308795.png",
    alt: 'Modern glass office building with green plants visible through windows reflecting sustainable architecture',
    icon: 'DocumentChartBarIcon',
    features: [
    {
      title: 'Compliance Assessment',
      description: 'Gap analysis and regulatory compliance mapping'
    },
    {
      title: 'Data Analytics',
      description: 'Advanced analytics and performance benchmarking'
    },
    {
      title: 'Report Generation',
      description: 'Automated ESG reporting for multiple frameworks'
    },
    {
      title: 'Stakeholder Communication',
      description: 'Investor-ready reports and stakeholder engagement'
    }],
    priceRange: {
      inr: '3,00,000',
      aed: '13,000',
      usd: '3,500'
    }
  },
  {
    title: 'Water Conservation Programs',
    description:
    'Innovative water management solutions that reduce consumption, improve efficiency, and achieve certification. Measure, optimize, and certify your water conservation efforts.',
    image: "https://images.unsplash.com/photo-1670921390802-032227fe3116",
    alt: 'Crystal clear water droplets on green leaf surface showing water conservation and natural purity',
    icon: 'BeakerIcon',
    features: [
    {
      title: 'Water Audit',
      description: 'Comprehensive water usage assessment and analysis'
    },
    {
      title: 'Optimization Strategy',
      description: 'Customized water conservation implementation plans'
    },
    {
      title: 'Technology Integration',
      description: 'Smart water management system deployment'
    },
    {
      title: 'Certification Support',
      description: 'Water stewardship certification assistance'
    }],
    priceRange: {
      inr: '2,50,000',
      aed: '11,000',
      usd: '3,000'
    }
  }];


  const certifications: Certification[] = [
  {
    name: 'ISO 14064',
    issuer: 'International Organization for Standardization',
    icon: 'ShieldCheckIcon',
    region: 'International'
  },
  {
    name: 'Verra VCS',
    issuer: 'Verified Carbon Standard',
    icon: 'CheckBadgeIcon',
    region: 'International'
  },
  {
    name: 'Gold Standard',
    issuer: 'Gold Standard Foundation',
    icon: 'StarIcon',
    region: 'International'
  },
  {
    name: 'BIS Certification',
    issuer: 'Bureau of Indian Standards',
    icon: 'DocumentCheckIcon',
    region: 'India'
  },
  {
    name: 'ESMA Approved',
    issuer: 'Emirates Authority for Standardization',
    icon: 'BuildingLibraryIcon',
    region: 'UAE'
  },
  {
    name: 'GRI Standards',
    issuer: 'Global Reporting Initiative',
    icon: 'GlobeAltIcon',
    region: 'International'
  },
  {
    name: 'TCFD Aligned',
    issuer: 'Task Force on Climate-related Disclosures',
    icon: 'ChartBarIcon',
    region: 'International'
  },
  {
    name: 'AWS Certification',
    issuer: 'Alliance for Water Stewardship',
    icon: 'BeakerIcon',
    region: 'International'
  }];


  const comparisonFeatures: ComparisonFeature[] = [
  {
    name: 'Initial Assessment',
    basic: true,
    professional: true,
    enterprise: true
  },
  {
    name: 'Project Development',
    basic: 'Single Project',
    professional: 'Up to 3 Projects',
    enterprise: 'Unlimited'
  },
  {
    name: 'Verification Support',
    basic: false,
    professional: true,
    enterprise: true
  },
  {
    name: 'ESG Reporting',
    basic: 'Annual',
    professional: 'Quarterly',
    enterprise: 'Monthly'
  },
  {
    name: 'Dedicated Account Manager',
    basic: false,
    professional: true,
    enterprise: true
  },
  {
    name: 'Custom Analytics Dashboard',
    basic: false,
    professional: 'Standard',
    enterprise: 'Advanced'
  },
  {
    name: 'Training & Workshops',
    basic: '1 Session',
    professional: '4 Sessions',
    enterprise: 'Unlimited'
  },
  {
    name: 'Priority Support',
    basic: false,
    professional: 'Business Hours',
    enterprise: '24/7'
  },
  {
    name: 'Multi-Region Support',
    basic: false,
    professional: false,
    enterprise: true
  },
  {
    name: 'White-Label Reporting',
    basic: false,
    professional: false,
    enterprise: true
  }];


  const testimonials: Testimonial[] = [
  {
    name: 'Senior Executive',
    role: 'Sustainability Director',
    company: 'Leading Technology Company',
    region: 'India',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd2c90b8-1765277218204.png",
    alt: 'Professional business executive in formal attire in modern office setting',
    quote:
    'GenCarbon transformed our approach to carbon credits. Their expertise helped us generate 50,000 tonnes of verified carbon credits in our first year.',
    impact: '50,000 tonnes CO₂ credits generated'
  },
  {
    name: 'ESG Manager',
    role: 'ESG Manager',
    company: 'Green Solutions Provider',
    region: 'UAE',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_141caecd3-1764670064142.png",
    alt: 'Professional business executive in formal attire presenting sustainability metrics',
    quote:
    'The ESG reporting solutions provided by GenCarbon exceeded our expectations. We achieved full compliance with international standards within 6 months.',
    impact: '100% ESG compliance achieved'
  },
  {
    name: 'Operations Head',
    role: 'Operations Head',
    company: 'Manufacturing Company',
    region: 'India',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cb2a7942-1765208481062.png",
    alt: 'Professional business executive reviewing sustainability reports in corporate environment',
    quote:
    'The water conservation program reduced our industrial water consumption by 40% while maintaining production efficiency. Remarkable results!',
    impact: '40% water consumption reduction'
  },
  {
    name: 'CEO',
    role: 'CEO',
    company: 'Innovation Company',
    region: 'UAE',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd2c90b8-1765277218204.png",
    alt: 'Business executive in professional attire in modern office setting',
    quote:
    'Dr. Nupur and her team provided invaluable guidance on our carbon credit journey. Their scientific approach and regional expertise made all the difference.',
    impact: '₹2.5 Cr revenue from carbon credits'
  },
  {
    name: 'Sustainability Consultant',
    role: 'Sustainability Consultant',
    company: 'Consultancy Firm',
    region: 'India',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17b5b1dd8-1764661633248.png",
    alt: 'Professional consultant presenting sustainability strategies in conference setting',
    quote:
    'GenCarbon\'s comprehensive approach to ESG reporting has become our benchmark. Their analytics platform provides insights we never had before.',
    impact: '300% improvement in ESG metrics'
  },
  {
    name: 'Regional Director',
    role: 'Regional Director',
    company: 'Sustainability Partners',
    region: 'International',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e5d00139-1765100302061.png",
    alt: 'International business executive in professional attire in corporate environment',
    quote:
    'Working with GenCarbon across multiple regions has been seamless. Their understanding of both Indian and UAE markets is exceptional.',
    impact: 'Multi-region ESG compliance'
  }];


  return (
    <div className="min-h-screen bg-background">
      <ServiceHero />

      {/* Services Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Our Core Services
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Comprehensive sustainability solutions tailored to your organization's unique needs and goals
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) =>
            <ServiceCard key={index} {...service} />
            )}
          </div>
        </div>
      </section>

      <CertificationBadges certifications={certifications} />
      <ServiceComparison features={comparisonFeatures} />
      <RegionalTestimonials testimonials={testimonials} />
      <ConsultationCTA />
    </div>);

};

export default OurServicesInteractive;